// assets/custom-product.js
(function () {
  "use strict";

  // ---------- Helpers ----------
  const toNum = (v) => {
    if (v == null) return NaN;
    return parseFloat(String(v).replace(",", "."));
  };

  // General formatter (uses storefront locale)
  const makeFormatter = (locale, currency) => {
    try {
      return new Intl.NumberFormat(locale || undefined, {
        style: "currency",
        currency: currency || "EUR",
        currencyDisplay: "symbol",
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
      });
    } catch {
      return { format: (n) => `€${Number(n).toFixed(2)}` };
    }
  };

  // Dot-decimal formatter (force “.” as decimal)
  const makeDotFormatter = (currency) => {
    try {
      // en-IE → € and dot-decimal, comma thousands
      return new Intl.NumberFormat("en-IE", {
        style: "currency",
        currency: currency || "EUR",
        currencyDisplay: "symbol",
        maximumFractionDigits: 2,
        minimumFractionDigits: 2
      });
    } catch {
      return { format: (n) => `€${Number(n).toFixed(2)}` };
    }
  };

  const getVariantPriceCents = (root) => {
    const cents = parseInt(root.getAttribute("data-variant-price-cents") || "0", 10);
    return Number.isFinite(cents) ? cents : 0;
  };

  // ---------- Core ----------
  const initSection = (root) => {
    if (!root) return;

    // Config
    const sectionId = root.getAttribute("data-section-id");
    const locale    = root.getAttribute("data-locale") || document.documentElement.lang || "en";
    const currency  = root.getAttribute("data-currency") || "EUR";
    const coverage  = toNum(root.getAttribute("data-coverage")); // m² per tile/box
    const unitType  = (root.getAttribute("data-unit-type") || "").trim(); // e.g., m², box, pack

    // Elements (scoped)
    const qtyTiles    = root.querySelector("[data-quantity-input]");
    const qtyM2       = root.querySelector("[data-m2-input]");
    const wastage     = root.querySelector("[data-wastage]");
    const totalEl     = root.querySelector("[data-total]");
    const unitTypeEl  = root.querySelector("[data-unit-price]");          // our “€X.XX / unitType” label
    const unitRow     = root.querySelector("[data-unit-price-row]");      // legal unit price row container

    // If the calculator UI isn't present (e.g., unit products), do nothing.
    if (!qtyTiles || !qtyM2 || !totalEl || !coverage || coverage <= 0) {
      // Still render unitType price if requested
      renderUnitTypePriceOnly();
      renderLegalUnitPrice(); // if unitRow exists
      return;
    }

    const fmt      = makeFormatter(locale, currency);
    const fmtDot   = makeDotFormatter(currency); // force dot-decimal where required

    let baseM2 = null; // remember pre-wastage m²

    // ---- Calculations ----
    const tilesFromM2 = (m2) => {
      if (!Number.isFinite(m2) || m2 <= 0 || coverage <= 0) return null;
      return Math.ceil(m2 / coverage);
    };

    const m2FromTiles = (tiles) => {
      if (!Number.isFinite(tiles) || tiles <= 0 || coverage <= 0) return null;
      return tiles * coverage;
    };

    // ---- Renderers ----
    function renderUnitTypePriceOnly() {
      // “€X.XX / {unitType}” — uses current variant price
      if (!unitTypeEl) return;
      const cents = getVariantPriceCents(root);
      const price = cents / 100;
      const label = unitType ? `${fmt.format(price)} / ${unitType}` : fmt.format(price);
      unitTypeEl.textContent = label;
    }

    function renderLegalUnitPrice() {
      // The “legal” unit price (per reference unit) — MUST show dot decimal.
      // Needs data on the container: data-unit-price-cents, data-unit-ref, data-unit-ref-value
      if (!unitRow || !unitTypeEl) return;
      const cents  = parseInt(unitRow.getAttribute("data-unit-price-cents") || "0", 10);
      const ref    = (unitRow.getAttribute("data-unit-ref") || "").trim();          // e.g., m², L, kg
      const refVal = unitRow.getAttribute("data-unit-ref-value");                    // e.g., 1, 100
      if (!Number.isFinite(cents) || cents <= 0) {
        // No legal unit price for this variant
        unitTypeEl.dataset.hasLegal = "0";
        return;
      }
      // Format with dot decimal regardless of storefront locale
      const formatted = makeDotFormatter(currency).format(cents / 100);
      // We don’t overwrite unitTypeEl here; legal unit price is expected to be printed
      // inside its own <span data-unit-price> within the [data-unit-price-row].
      const legalSpan = unitRow.querySelector("[data-unit-price]");
      if (legalSpan) {
        legalSpan.textContent = refVal ? `${formatted} per ${refVal}${ref}` : `${formatted} per ${ref}`;
      }
      unitTypeEl.dataset.hasLegal = "1";
    }

    function renderTotal() {
      const tiles = toNum(qtyTiles.value);
      if (!Number.isFinite(tiles) || tiles <= 0) {
        totalEl.textContent = fmt.format(0);
        return;
      }
      const total = (getVariantPriceCents(root) / 100) * tiles;
      totalEl.textContent = fmt.format(total);
    }

    // ---- Syncers ----
    function syncTilesFromM2() {
      const m2 = toNum(qtyM2.value);
      const tiles = tilesFromM2(m2);
      if (tiles != null) qtyTiles.value = tiles;
    }

    function syncM2FromTiles() {
      const tiles = toNum(qtyTiles.value);
      const m2 = m2FromTiles(tiles);
      if (m2 != null) qtyM2.value = m2.toFixed(2);
    }

    // ---- Wastage ----
    function applyWastage() {
      baseM2 = toNum(qtyM2.value);
      if (!Number.isFinite(baseM2) || baseM2 <= 0) return;
      const m2With = baseM2 * 1.10;
      qtyM2.value = m2With.toFixed(2);
      syncTilesFromM2();
      renderTotal();
    }

    function removeWastage() {
      if (!Number.isFinite(baseM2) || baseM2 <= 0) return;
      qtyM2.value = baseM2.toFixed(2);
      syncTilesFromM2();
      renderTotal();
    }

    function clearWastageIfChanged() {
      if (wastage && wastage.checked) {
        wastage.checked = false;
        removeWastage();
        baseM2 = null;
      }
    }

    // ---- Events ----
    qtyM2.addEventListener("input", () => {
      syncTilesFromM2();
      clearWastageIfChanged();
      renderTotal();
    });

    ["input", "change"].forEach((ev) => {
      qtyTiles.addEventListener(ev, () => {
        syncM2FromTiles();
        clearWastageIfChanged();
        renderTotal();
      });
    });

    if (wastage) {
      wastage.addEventListener("change", () => {
        if (wastage.checked) applyWastage();
        else {
          removeWastage();
          baseM2 = null;
        }
      });
    }

    // React to variant changes (Dawn emits this; we also add a fallback below)
    root.addEventListener("variant:change", (e) => {
      const variant = e?.detail?.variant;
      if (variant) {
        // Keep our price-in-cents attribute in sync
        root.setAttribute("data-variant-price-cents", String(variant.price || 0));

        // Update legal unit price cents on the row (if present)
        if (unitRow && typeof variant.unit_price === "number") {
          unitRow.setAttribute("data-unit-price-cents", String(variant.unit_price));
        }
      }
      renderUnitTypePriceOnly();
      renderLegalUnitPrice();
      renderTotal();
    });

    // Fallback if theme doesn’t dispatch variant:change
    const idInput = root.querySelector('input[name="id"], select[name="id"]');
    if (idInput) {
      idInput.addEventListener("change", () => {
        // If your product-form.js sets data-variant-price-cents, we pick it up automatically.
        // Otherwise, totals will keep using the last known value.
        renderUnitTypePriceOnly();
        renderLegalUnitPrice();
        renderTotal();
      });
    }

    // ---- Initial paint ----
    renderUnitTypePriceOnly();
    syncM2FromTiles(); // set initial m² from initial qty
    renderLegalUnitPrice();
    renderTotal();
  };

  // ---------- Boot ----------
  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("[data-product-section]").forEach(initSection);
  });



})();
